<template>
  <div>
    <div class="log-contact-popover-backdrop" @click="cancel"></div>
    <div ref="popover" class="log-contact-popover" :class="{ 'is-flipped': flipUp }" @click.stop>
      <h5 class="m-t-none m-b-sm font-semi">Log contact</h5>

      <div class="form-group m-b-sm">
        <label class="text-12 text-gray-600 m-b-xxs">Contact type</label>
        <select v-model="activityType" class="form-control input-sm">
          <option value="">Select type...</option>
          <option v-for="at in activityTypes" :key="at.value" :value="at.value">
            {{ at.label }}
          </option>
        </select>
      </div>

      <div class="form-group m-b-sm">
        <label class="text-12 text-gray-600 m-b-xxs">Details</label>
        <textarea
          v-model="noteText"
          class="form-control"
          rows="3"
          placeholder="Brief note..."
          @keydown.ctrl.enter="save"
          @keydown.meta.enter="save"
        ></textarea>
      </div>

      <div v-if="actionableWrestlers.length > 0" class="form-group m-b-sm">
        <label class="text-12 text-gray-600 m-b-xxs">Update stage (optional)</label>
        <div
          v-for="child in actionableWrestlers"
          :key="'stage-' + child.id"
          class="flex align-items-center gap-x-xs m-b-xxs"
        >
          <span class="text-14 font-semi flex-1" style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            {{ child.child_name || 'Unnamed' }}
          </span>
          <select
            v-model="stageChoices[child.id]"
            class="form-control input-sm"
            style="width: auto; display: inline-block;"
          >
            <option value="">— No change —</option>
            <option v-for="s in stageOptions" :key="child.id + '-' + s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </div>
      </div>

      <div v-if="actionableWrestlers.length > 0" class="form-group m-b-sm">
        <label class="text-12 text-gray-600 m-b-xxs">Change follow-up</label>
        <div
          v-for="child in actionableWrestlers"
          :key="'fu-' + child.id"
          class="flex align-items-center gap-x-xs m-b-xxs"
        >
          <span class="text-14 font-semi flex-1" style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            {{ child.child_name || 'Unnamed' }}
          </span>
          <select
            v-model="followUpChoices[child.id]"
            class="form-control input-sm"
            style="width: auto; display: inline-block;"
          >
            <option value="">— No change —</option>
            <option v-if="child.needs_follow_up" value="clear">Clear follow-up</option>
            <option v-else value="add">Add follow-up</option>
          </select>
        </div>
      </div>

      <div v-if="error" class="text-12 text-red-600 m-b-xs">{{ error }}</div>

      <div class="flex justify-space-between">
        <button type="button" class="btn btn-default btn-sm" @click="cancel">Cancel</button>
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <span v-if="saving">Saving...</span>
          <span v-else>Save</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'log-contact-popover',
  props: {
    createUrl: { type: String, required: true },
    family: { type: Object, default: null },
    activityTypes: {
      type: Array,
      default: () => [
        { value: 'phone_call', label: 'Phone Call' },
        { value: 'sms', label: 'Text' },
        { value: 'email', label: 'Email' },
        { value: 'in_person', label: 'In Person' },
        { value: 'other', label: 'Other' },
      ],
    },
  },
  data() {
    return {
      activityType: 'phone_call',
      noteText: '',
      stageChoices: {},
      // Per-child string: '' (no change) | 'clear' | 'add'.
      followUpChoices: {},
      saving: false,
      error: '',
      // True when there's not enough room below the trigger button to fit
      // the popover; flips it above so it doesn't get cut off the bottom
      // of the viewport. Decided once on mount.
      flipUp: false,
    };
  },
  computed: {
    // Any active-stage prospect can take a stage update in the popover.
    actionableWrestlers() {
      if (!this.family || !this.family.prospects) return [];
      const actionable = ['inquiry', 'trial_scheduled', 'trialing', 'trial_complete'];
      return this.family.prospects.filter((p) => actionable.includes(p.stage));
    },
    stageOptions() {
      return [
        { value: 'trial_scheduled', label: 'Trial Scheduled' },
        { value: 'trialing', label: 'Trialing' },
        { value: 'trial_complete', label: 'Trial Complete' },
        { value: 'converted', label: 'Converted' },
        { value: 'didnt_join', label: "Didn't Join" },
        { value: 'archived', label: 'Archived' },
      ];
    },
  },
  created() {
    // Pre-seed reactive entries so v-model binds cleanly.
    this.actionableWrestlers.forEach((child) => {
      this.$set(this.stageChoices, child.id, '');
      this.$set(this.followUpChoices, child.id, '');
    });
  },
  mounted() {
    // The popover is absolutely positioned below its trigger. When the
    // trigger is near the bottom of the viewport (last lead in a long
    // queue) the popover overflows the page and the user can't scroll
    // to it. Measure once on mount and flip above when we'd be cut off.
    this.$nextTick(() => {
      const el = this.$refs.popover;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const overflowsBottom = rect.bottom > window.innerHeight - 16;
      const triggerTop = el.parentElement?.getBoundingClientRect().top ?? 0;
      // Only flip if there's actually more room above than below; otherwise
      // we'd just trade one overflow for another.
      if (overflowsBottom && triggerTop > rect.height + 16) {
        this.flipUp = true;
      }
    });
  },
  methods: {
    cancel() {
      this.$emit('cancel');
    },
    save() {
      if (!this.activityType) {
        this.error = 'Select a contact type.';
        return;
      }
      if (!this.noteText.trim()) {
        this.error = 'Add a brief note.';
        return;
      }
      this.error = '';
      this.saving = true;

      const plain = this.noteText.trim();
      const escaped = plain
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');

      const params = {
        note: {
          content: `<div>${escaped}</div>`,
          plain_content: plain,
          activity_type: this.activityType,
        },
      };

      const clearIds = Object.keys(this.followUpChoices)
        .filter((id) => this.followUpChoices[id] === 'clear')
        .map((id) => parseInt(id, 10));
      const addIds = Object.keys(this.followUpChoices)
        .filter((id) => this.followUpChoices[id] === 'add')
        .map((id) => parseInt(id, 10));
      if (clearIds.length > 0) params.clear_follow_up_for = clearIds;
      if (addIds.length > 0) params.add_follow_up_for = addIds;

      axios.post(this.createUrl, params)
        .then(() => {
          this.saving = false;
          this.$notificationManager.emit('show-toast', 'Contact logged', true);
          this.emitStageChanges();
          this.$emit('logged');
        })
        .catch(() => {
          this.saving = false;
          this.error = 'Error logging contact.';
        });
    },
    emitStageChanges() {
      this.actionableWrestlers.forEach((child) => {
        const picked = this.stageChoices[child.id];
        if (!picked || picked === child.stage) return;
        this.$emit('stage-change', { child, newStage: picked, extra: {} });
      });
    },
  },
};
</script>
